import { useState } from "react";
import { Plus } from "lucide-react";

import { LaborStationOperatorPresence } from "@/components/labor/LaborStationOperatorPresence";
import { Button } from "@/components/ui/Button";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { playPairFailureSound } from "@/lib/floorStationPairAudio";
import { hubViewportInboxShellClassName } from "@/lib/hubLayoutClassNames";

import { InboundArrivalLog } from "./InboundArrivalLog";
import { InboundStationLaborTracker } from "./InboundStationLaborTracker";
import { InboundZplPrinterHeaderButton } from "./InboundZplPrinterHeaderButton";
import { LogInboundArrivalModal } from "./LogInboundArrivalModal";
import { receivingLaborWirePage } from "./inboundLaborPage";
import { useInboundZplPrinter } from "./inboundZplPrinter";
import type { InboundDockShipment, LogInboundArrivalPrefill } from "./inboundApi";

const ARRIVALS_HOVER_HINT =
  "Incoming purchase orders and freight logged at the dock, grouped as one shipment. "
  + "Tap a row to see each carton label, reprint or cancel individual crates, or add another delivery. "
  + "Use New dock arrival for freight that is not on the list yet.";

function prefillFromShipment(shipment: InboundDockShipment): LogInboundArrivalPrefill {
  return {
    vendor_name: shipment.vendor_name,
    po_number: shipment.po_number,
    tracking: shipment.expected_tracking ?? undefined,
    linked_source_type: shipment.linked_source_type,
    linked_source_id:
      shipment.linked_source_id != null ? Number(shipment.linked_source_id) : undefined,
  };
}

export function InboundArrivalsPage(): JSX.Element {
  const { station, setStation, selectedPrinter, unavailableMessage } = useInboundZplPrinter();
  const [createOpen, setCreateOpen] = useState(false);
  const [createPrefill, setCreatePrefill] = useState<LogInboundArrivalPrefill | undefined>(undefined);
  const [printNotice, setPrintNotice] = useState<string | null>(null);

  function openNewArrival(prefill?: LogInboundArrivalPrefill): void {
    setPrintNotice(null);
    setCreatePrefill(prefill);
    setCreateOpen(true);
  }

  return (
    <div className={hubViewportInboxShellClassName()}>
      <InboundStationLaborTracker
        wirePage={receivingLaborWirePage("arrivals")}
        workLabel="Arrivals"
        enabled={createOpen}
        leaveFloorOnStop={false}
      />
      <header className="flex shrink-0 flex-wrap items-end justify-between gap-3">
        <PageTitleWithHelp hoverHint={ARRIVALS_HOVER_HINT}>Arrivals</PageTitleWithHelp>
        <div className="flex min-w-0 max-w-full flex-wrap items-end gap-2">
          <LaborStationOperatorPresence stationPage="receiving" receivingFloor="arrivals" />
          <InboundZplPrinterHeaderButton
            station={station}
            onStationChange={setStation}
            unavailableMessage={unavailableMessage}
          />
          <Button type="button" onClick={() => openNewArrival()}>
            <Plus className="h-4 w-4" />
            New dock arrival
          </Button>
        </div>
      </header>
      <InboundArrivalLog
        selectedPrinter={selectedPrinter}
        printNotice={printNotice}
        onClearPrintNotice={() => setPrintNotice(null)}
        onRequestNew={() => openNewArrival()}
        onAddArrival={(shipment) => openNewArrival(prefillFromShipment(shipment))}
      />
      <LogInboundArrivalModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreatePrefill(undefined);
        }}
        onLogged={(nextPrintError) => {
          setCreateOpen(false);
          setCreatePrefill(undefined);
          if (nextPrintError != null && nextPrintError.trim() !== "") {
            playPairFailureSound();
          }
          setPrintNotice(nextPrintError);
        }}
        selectedPrinter={selectedPrinter}
        prefill={createPrefill}
      />
    </div>
  );
}
